import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import cc, { CurrencyCodeRecord } from 'currency-codes';
import { Formik } from 'formik';

import { grid, distances } from '../../../styles/constants';
import { Transaction } from '../../../types/checkout';
import { OperationItem } from '../../types';
import { getRefundableAmount } from '../../transactionHelpers';
import { formatMoney, toMonetaryAmountString, fromMonetaryAmountString } from '../../../helpers/formatters';
import { getLocaleNumeralDelimiter, getLocaleNumeralDecimal } from '../../../helpers/intl';

import { P } from '../../../components/Typography';
import { FormattedInput, ValidationState } from '../../../components/Forms';
import { Button, ButtonGroup } from '../../../components/Buttons';

type RefundByAmountProps = {
    transaction: Transaction;
    refundTransaction: (
        accountId: string,
        transaction: Transaction,
        amount: number,
        captureItems: OperationItem[]
    ) => Promise<Transaction | undefined>;
    accountId: string;
    isLoading: boolean;
    showSuccessMessage: (refundAmount: number, transaction: Transaction) => void;
};

interface FormikErrors {
    amountToRefund?: string;
}

const RefundByAmount = ({
    transaction,
    refundTransaction,
    accountId,
    isLoading,
    showSuccessMessage,
}: RefundByAmountProps) => {
    const { t, i18n } = useTranslation();

    const canOnlyRefundFullAmount =
        transaction.payment_product === 'instabank' || transaction.payment_product === 'collector';
    const refundableAmount = getRefundableAmount(transaction);
    const initalRefundAmount = canOnlyRefundFullAmount ? refundableAmount : 0;
    const exponent = cc.code(transaction.currency) ? (cc.code(transaction.currency) as CurrencyCodeRecord).digits : 2;
    const lng = i18n.language;
    return (
        <Wrapper>
            <Formik
                initialValues={{
                    amountToRefund: toMonetaryAmountString(lng, initalRefundAmount, transaction.currency),
                }}
                validate={(values) => {
                    const errors: FormikErrors = {};
                    if (!values.amountToRefund) {
                        errors.amountToRefund = t('payments.refund.form.validation.amount_missing');
                    }
                    const num = fromMonetaryAmountString(lng, values.amountToRefund, transaction.currency);
                    if (
                        isNaN(num) ||
                        (num <= 0 &&
                            transaction.payment_product_type !== 'dintero.zero')
                    ) {
                        errors.amountToRefund = t(
                            'payments.refund.form.validation.amount_invalid'
                        );
                    }

                    if (num > refundableAmount) {
                        errors.amountToRefund = t('payments.refund.form.validation.amount_too_high');
                    }
                    return errors;
                }}
                onSubmit={(values) => {
                    const refundAmount = fromMonetaryAmountString(lng, values.amountToRefund, transaction.currency);
                    refundTransaction(
                        accountId,
                        transaction,
                        refundAmount,
                        []
                    ).then(result => {
                        if (result) {
                            showSuccessMessage(refundAmount, result);
                        }
                    });
                }}
            >
                {({ values, errors, handleChange, handleBlur, handleSubmit }) => (
                    <form onSubmit={handleSubmit} autoComplete="nope">
                        <AmountArea>
                            <P>
                                {refundableAmount === transaction.amount ? (
                                    <>
                                        {t('payments.refund.form.amount_available_pristine', {
                                            amount: formatMoney(lng, refundableAmount, transaction.currency),
                                        })}
                                    </>
                                ) : (
                                    <>
                                        {t('payments.refund.form.amount_available_partial', {
                                            amount: formatMoney(lng, refundableAmount, transaction.currency),
                                        })}
                                    </>
                                )}
                            </P>
                            <InputWrapper>
                                <FormattedInput
                                    options={{
                                        numeral: true,
                                        numericOnly: true,

                                        numeralThousandsGroupStyle: 'thousand',
                                        delimiter: getLocaleNumeralDelimiter(lng),
                                        numeralIntegerScale: `${refundableAmount}`.length,
                                        numeralDecimalMark: getLocaleNumeralDecimal(lng),
                                        numeralPositiveOnly: true,
                                        numeralDecimalScale: exponent,
                                    }}
                                    label={t('payments.refund.form.amount_label')}
                                    placeholder=""
                                    type="tel"
                                    inputMode="numeric"
                                    name="amountToRefund"
                                    value={values.amountToRefund}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    prefix={transaction.currency}
                                    disabled={canOnlyRefundFullAmount}
                                    validation={{
                                        state: errors.amountToRefund
                                            ? ValidationState.Invalid
                                            : ValidationState.Pristine,
                                        message: errors.amountToRefund,
                                    }}
                                />
                            </InputWrapper>
                        </AmountArea>
                        <ButtonGroup>
                            <CaptureButton
                                disabled={isLoading || errors.amountToRefund ? true : false}
                                className="capture"
                                type="submit"
                            >
                                {t('payments.refund.form.refund_button', {
                                    amount: formatMoney(
                                        lng,
                                        fromMonetaryAmountString(
                                            lng,
                                            values.amountToRefund || '0',
                                            transaction.currency
                                        ),
                                        transaction.currency
                                    ),
                                })}
                            </CaptureButton>
                        </ButtonGroup>
                    </form>
                )}
            </Formik>
        </Wrapper>
    );
};

export default RefundByAmount;

const Wrapper = styled.div`
    position: relative;
    min-width: ${grid.spans.span5};
`;

const AmountArea = styled.div`
    padding-top: ${distances.tiny};
    padding-bottom: ${distances.tiny};
`;

const InputWrapper = styled.div`
    width: 220px;
`;

const CaptureButton = styled(Button)`
    min-width: 220px;
`;
